import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, ShoppingBag, CheckCircle2 } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";
import ProductCard, { StockBadge } from "../components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const { waLink, settings } = useSettings();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [imgIndex, setImgIndex] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setProduct(null);
    setImgIndex(0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get(`/products/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [id]);

  if (notFound)
    return (
      <div className="container-oxy py-20 text-center" data-testid="product-not-found">
        <h1 className="font-display text-2xl font-bold text-slate-900">Produit introuvable</h1>
        <Link to="/catalogue" className="btn-ink mt-6">Retour au catalogue</Link>
      </div>
    );

  if (!product)
    return <div className="container-oxy py-16"><div className="aspect-[4/3] animate-pulse rounded-3xl bg-slate-100" /></div>;

  const reservable = product.status === "available" && !product.out_of_stock;
  const images = product.images?.length ? product.images : [];
  const hasWhatsApp = Boolean((settings?.whatsapp_number || "").trim());

  return (
    <div className="container-oxy pt-6 sm:pt-10" data-testid="product-detail-page">
      <Link to="/catalogue" data-testid="back-to-catalog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-gold-dark">
        <ArrowLeft className="h-4 w-4" /> Catalogue
      </Link>

      <div className="mt-4 grid gap-8 lg:grid-cols-2">
        <div>
          <div className="overflow-hidden rounded-3xl border border-amber-500/20 bg-white shadow-sm">
            {images.length ? (
              <img src={fileUrl(images[imgIndex])} alt={product.name} data-testid="product-main-image"
                className="aspect-[4/3] w-full object-cover" />
            ) : (
              <div className="flex aspect-[4/3] items-center justify-center bg-slate-100 text-slate-400">Photo à venir</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="product-thumbnails">
              {images.map((img, i) => (
                <button key={i} data-testid={`product-thumb-${i}`} onClick={() => setImgIndex(i)}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${i === imgIndex ? "border-gold" : "border-transparent opacity-60"}`}>
                  <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="eyebrow">{product.category}</span>
            <StockBadge product={product} />
            {product.demo && <span className="rounded-full bg-ink/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold">Exemple</span>}
          </div>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{product.name}</h1>
          {product.condition && <p className="mt-1 text-sm font-semibold text-slate-500">{product.condition}{product.brand ? ` · ${product.brand}` : ""}</p>}
          <p className="mt-5 font-display text-4xl font-extrabold text-ink" data-testid="product-price">{product.price} €</p>

          {product.description && (
            <p className="mt-5 leading-relaxed text-slate-600" data-testid="product-description">{product.description}</p>
          )}

          {product.specs?.length > 0 && (
            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200" data-testid="product-specs">
              {product.specs.map((s, i) => (
                <div key={i} className={`flex justify-between gap-4 px-4 py-3 text-sm ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                  <span className="font-semibold text-slate-600">{s.key}</span>
                  <span className="text-right text-slate-900">{s.value}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            {reservable ? (
              <Link to={`/reserver/${product.id}`} data-testid="product-reserve-btn" className="btn-gold flex-1">
                <ShoppingBag className="h-4 w-4" /> Réserver en boutique
              </Link>
            ) : (
              <span data-testid="product-unavailable-note" className="flex-1 rounded-full bg-slate-100 px-6 py-3 text-center font-display font-semibold text-slate-500">
                Non réservable actuellement
              </span>
            )}
            {hasWhatsApp && (
              <a
                href={waLink(`Bonjour Oxyservices, je suis intéressé(e) par : ${product.name} (${product.price} €). Est-il toujours disponible ?`)}
                target="_blank" rel="noopener noreferrer" data-testid="product-whatsapp-btn"
                className="btn-outline-oxy flex-1">
                <MessageCircle className="h-4 w-4" /> Question WhatsApp
              </a>
            )}
          </div>
          <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-slate-500">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            Réservation gratuite, sans paiement en ligne. Retrait et paiement en boutique.
          </p>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Vous aimerez aussi</h2>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
